import { useEffect, useState } from "react";
import { NavLink, Route, Routes, useNavigate, Link } from "react-router-dom";
import {
    LineChart, Line, AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import Navbar from "../../components/layout/Navbar";
import {
    LayoutDashboard, Users, Package, Store, ShoppingBag, CreditCard, Tag,
    TrendingUp, TrendingDown, ArrowUpRight, Check, X, Search,
    Wine, Sprout, Calendar, Activity, Layers, UserCheck, Wallet, FileBarChart, BarChart3, Truck, Gift,
} from "lucide-react";
import { toast } from "sonner";
import AdminProducerDetail from "./AdminProducerDetail";
import AdminProductDetail from "./AdminProductDetail";
import AdminCategories from "./AdminCategories";
import AdminCustomers from "./AdminCustomers";
import AdminPayments from "./AdminPayments";
import AdminPayouts from "./AdminPayouts";
import AdminFinancialReports from "./AdminFinancialReports";
import AdminMarketplaceAnalytics from "./AdminMarketplaceAnalytics";
import AdminShipments from "./AdminShipments";
import AdminReferrals from "./AdminReferrals";

const WINE = "#7A2A36";
const GOLD = "#D4A93C";
const VINE = "#4F7942";

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    useEffect(() => {
        api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    }, []);
    return (
        <>
            <Navbar />
            <section className="admin-area pt-28 pb-20 bg-white min-h-[80vh]">
                <div className="container-edge grid grid-cols-1 lg:grid-cols-12 gap-8">
                    <Sidebar stats={stats} />
                    <div className="lg:col-span-10 space-y-8">
                        <TopBar />
                        <Routes>
                            <Route index element={<Overview />} />
                            <Route path="produtores" element={<ProducersAdmin />} />
                            <Route path="produtores/:id" element={<AdminProducerDetail />} />
                            <Route path="produtos" element={<ProductsAdmin />} />
                            <Route path="produtos/:id" element={<AdminProductDetail />} />
                            <Route path="vinhos" element={<WinesAdmin />} />
                            <Route path="categorias" element={<AdminCategories />} />
                            <Route path="clientes" element={<AdminCustomers />} />
                            <Route path="encomendas" element={<OrdersAdmin />} />
                            <Route path="envios" element={<AdminShipments />} />
                            <Route path="pagamentos" element={<AdminPayments />} />
                            <Route path="comissoes" element={<AdminPayouts />} />
                            <Route path="afiliados" element={<AdminReferrals />} />
                            <Route path="relatorios" element={<AdminFinancialReports />} />
                            <Route path="analytics" element={<AdminMarketplaceAnalytics />} />
                            <Route path="utilizadores" element={<UsersAdmin />} />
                            <Route path="assinaturas" element={<SubsAdmin />} />
                            <Route path="cupoes" element={<CouponsAdmin />} />
                        </Routes>
                    </div>
                </div>
            </section>
        </>
    );
}

function TopBar() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    return (
        <header className="flex items-center justify-between bg-white rounded-md border border-gold-soft/30 px-6 py-4 shadow-sm" data-testid="admin-topbar">
            <div>
                <span className="label-overline text-wine">Painel administrativo</span>
                <h1 className="font-heading text-2xl text-wine-dark leading-none mt-1">
                    Bem-vindo, {user?.name?.split(" ")[0] || "Admin"}
                </h1>
            </div>
            <div className="flex items-center gap-4">
                <span className="text-xs text-wine-dark/60 hidden md:flex items-center gap-2">
                    <Calendar className="h-4 w-4" strokeWidth={1.5} />
                    {new Date().toLocaleDateString("pt-PT", { weekday: "long", day: "numeric", month: "long" })}
                </span>
                <button
                    onClick={async () => { await logout(); nav("/"); }}
                    className="btn-wine-outline text-xs px-5 py-2.5"
                    data-testid="admin-logout"
                >
                    Sair
                </button>
            </div>
        </header>
    );
}

function Sidebar({ stats }) {
    const links = [
        { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
        { to: "/admin/produtores", label: "Produtores", icon: Store, badge: stats?.pending_producers },
        { to: "/admin/produtos", label: "Produtos", icon: Package, badge: stats?.pending_products },
        { to: "/admin/vinhos", label: "Vinhos", icon: Wine },
        { to: "/admin/categorias", label: "Categorias", icon: Layers },
        { to: "/admin/clientes", label: "Clientes", icon: UserCheck },
        { to: "/admin/encomendas", label: "Encomendas", icon: ShoppingBag },
        { to: "/admin/envios", label: "Envios", icon: Truck },
        { to: "/admin/pagamentos", label: "Pagamentos", icon: CreditCard },
        { to: "/admin/comissoes", label: "Comissões", icon: Wallet },
        { to: "/admin/afiliados", label: "Afiliados & Recompensas", icon: Gift },
        { to: "/admin/relatorios", label: "Relatórios", icon: FileBarChart },
        { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
        { to: "/admin/utilizadores", label: "Utilizadores", icon: Users },
        { to: "/admin/assinaturas", label: "Assinaturas", icon: Wine },
        { to: "/admin/cupoes", label: "Cupões", icon: Tag },
    ];
    return (
        <aside className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-md border border-gold-soft/30 p-5 shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-wine rounded-md flex items-center justify-center text-cream font-heading text-xl">
                        A
                    </div>
                    <div>
                        <span className="label-overline text-wine">Admin</span>
                        <p className="font-heading text-lg text-wine-dark leading-none mt-0.5">Caseirinho</p>
                    </div>
                </div>
            </div>

            <nav className="bg-white rounded-md border border-gold-soft/30 p-2 shadow-sm">
                {links.map(({ to, label, icon: Icon, end, badge }) => (
                    <NavLink
                        key={to}
                        to={to}
                        end={end}
                        className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-md text-xs transition-all duration-200 ${
                                isActive
                                    ? "bg-wine text-cream font-semibold"
                                    : "text-wine-dark hover:bg-wine/5"
                            }`
                        }
                        data-testid={`admin-nav-${label}`}
                    >
                        <Icon className="h-4 w-4" strokeWidth={1.5} />
                        <span className="flex-1 uppercase tracking-wider">{label}</span>
                        {badge > 0 && (
                            <span className="bg-gold-soft text-wine-dark text-[10px] font-bold rounded-full px-2 py-0.5">
                                {badge}
                            </span>
                        )}
                    </NavLink>
                ))}
            </nav>
        </aside>
    );
}

/* ---------- OVERVIEW ---------- */
function Overview() {
    const [stats, setStats] = useState(null);
    const [analytics, setAnalytics] = useState(null);

    useEffect(() => {
        Promise.all([api.get("/admin/stats"), api.get("/admin/analytics")])
            .then(([s, a]) => { setStats(s.data); setAnalytics(a.data); })
            .catch(() => {});
    }, []);

    if (!stats || !analytics) {
        return <p className="text-wine-dark/60 label-overline">A carregar…</p>;
    }

    const growth = analytics.period.revenue_growth_pct;

    return (
        <div className="space-y-8">
            {/* KPI grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard
                    label="Receita (30d)"
                    value={`€${analytics.period.revenue_30d.toFixed(2)}`}
                    growth={growth}
                    highlight
                    icon={TrendingUp}
                />
                <KpiCard
                    label="Comissões"
                    value={`€${stats.commissions.toFixed(2)}`}
                    sub="Acumulado"
                    icon={Sprout}
                />
                <KpiCard
                    label="Encomendas (30d)"
                    value={analytics.period.orders_30d}
                    sub={`Ticket médio €${analytics.period.avg_order_value.toFixed(2)}`}
                    icon={ShoppingBag}
                />
                <KpiCard
                    label="Clube ativo"
                    value={stats.active_subscriptions}
                    sub="Assinaturas"
                    icon={Wine}
                />
            </div>

            {/* Pending actions */}
            {(stats.pending_producers > 0 || stats.pending_products > 0) && (
                <div className="bg-gold-soft/15 border border-gold-soft/60 rounded-md p-5 flex flex-wrap items-center justify-between gap-4" data-testid="pending-actions">
                    <div className="flex items-center gap-3">
                        <Activity className="h-5 w-5 text-gold-deep" strokeWidth={1.5} />
                        <span className="text-wine-dark text-sm">
                            <strong>{stats.pending_producers}</strong> produtor(es) e{" "}
                            <strong>{stats.pending_products}</strong> produto(s) a aguardar aprovação.
                        </span>
                    </div>
                    <div className="flex gap-2">
                        {stats.pending_producers > 0 && (
                            <NavLink to="/admin/produtores" className="btn-wine-outline text-xs px-5 py-2">
                                Rever produtores
                            </NavLink>
                        )}
                        {stats.pending_products > 0 && (
                            <NavLink to="/admin/produtos" className="btn-wine text-xs px-5 py-2">
                                Rever produtos
                            </NavLink>
                        )}
                    </div>
                </div>
            )}

            {/* Revenue chart */}
            <div className="bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <span className="label-overline text-wine/60">Receita & Comissão</span>
                        <h2 className="font-heading text-2xl text-wine-dark mt-1">Últimos 30 dias</h2>
                    </div>
                    <div className="flex gap-5 text-xs">
                        <Legend color={WINE} label="Receita" />
                        <Legend color={GOLD} label="Comissão" />
                    </div>
                </div>
                <div className="h-72">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={analytics.revenue_series}>
                            <defs>
                                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={WINE} stopOpacity={0.35} />
                                    <stop offset="100%" stopColor={WINE} stopOpacity={0.02} />
                                </linearGradient>
                                <linearGradient id="comm" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={GOLD} stopOpacity={0.30} />
                                    <stop offset="100%" stopColor={GOLD} stopOpacity={0.02} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#7A2A36" strokeOpacity={0.08} vertical={false} />
                            <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} stroke="#7A2A36" strokeOpacity={0.4} fontSize={11} />
                            <YAxis stroke="#7A2A36" strokeOpacity={0.4} fontSize={11} tickFormatter={(v) => `€${v}`} />
                            <Tooltip
                                contentStyle={{ background: "#FBF8F3", border: "1px solid #D4A93C", borderRadius: 6, fontSize: 12 }}
                                labelStyle={{ color: "#7A2A36", fontWeight: 600 }}
                                formatter={(v, n) => [`€${Number(v).toFixed(2)}`, n === "revenue" ? "Receita" : "Comissão"]}
                            />
                            <Area type="monotone" dataKey="revenue" stroke={WINE} strokeWidth={2} fill="url(#rev)" />
                            <Area type="monotone" dataKey="commission" stroke={GOLD} strokeWidth={2} fill="url(#comm)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Two-col: top producers + activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Top producers */}
                <div className="lg:col-span-2 bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <span className="label-overline text-wine/60">Leaderboard</span>
                            <h2 className="font-heading text-2xl text-wine-dark mt-1">Produtores top</h2>
                        </div>
                        <NavLink to="/admin/produtores" className="label-overline text-wine hover:text-gold-deep">
                            Ver todos →
                        </NavLink>
                    </div>
                    {analytics.top_producers.length === 0 ? (
                        <p className="text-wine-dark/60 text-sm">Sem vendas ainda.</p>
                    ) : (
                        <div className="space-y-3">
                            {analytics.top_producers.map((p, i) => (
                                <div key={p.id} className="flex items-center gap-4 p-3 hover:bg-cream rounded-md transition-colors" data-testid={`top-producer-${i}`}>
                                    <span className="font-heading text-3xl text-wine-dark/30 w-10">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-heading text-lg text-wine-dark truncate">{p.name}</p>
                                        <p className="text-xs text-wine-dark/60">{p.region} · {p.orders} encomendas</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-heading text-lg text-wine-dark">€{p.sales.toFixed(2)}</p>
                                        <span className="label-overline text-vineyard">Vendas</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Activity feed */}
                <div className="bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <span className="label-overline text-wine/60">Tempo real</span>
                            <h2 className="font-heading text-2xl text-wine-dark mt-1">Atividade</h2>
                        </div>
                    </div>
                    {analytics.activity.length === 0 ? (
                        <p className="text-wine-dark/60 text-sm">Sem atividade recente.</p>
                    ) : (
                        <div className="space-y-3 max-h-[360px] overflow-y-auto pr-1">
                            {analytics.activity.map((a, i) => (
                                <ActivityItem key={`${a.at || ""}-${a.type || ""}-${i}`} item={a} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom: orders chart + top products */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                    <span className="label-overline text-wine/60">Encomendas / dia</span>
                    <h2 className="font-heading text-2xl text-wine-dark mt-1 mb-4">Volume diário</h2>
                    <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={analytics.revenue_series}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#7A2A36" strokeOpacity={0.08} vertical={false} />
                                <XAxis dataKey="date" tickFormatter={(d) => d.slice(8)} stroke="#7A2A36" strokeOpacity={0.4} fontSize={11} />
                                <YAxis stroke="#7A2A36" strokeOpacity={0.4} fontSize={11} allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{ background: "#FBF8F3", border: "1px solid #D4A93C", borderRadius: 6, fontSize: 12 }}
                                    formatter={(v) => [v, "Encomendas"]}
                                />
                                <Bar dataKey="orders" fill={VINE} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                    <span className="label-overline text-wine/60">Mais vendidos</span>
                    <h2 className="font-heading text-2xl text-wine-dark mt-1 mb-4">Top produtos</h2>
                    {analytics.top_products.length === 0 ? (
                        <p className="text-wine-dark/60 text-sm">Sem vendas ainda.</p>
                    ) : (
                        <div className="space-y-3">
                            {analytics.top_products.map((p, i) => (
                                <div key={p.id} className="flex items-center gap-3" data-testid={`top-product-${i}`}>
                                    <img src={p.images?.[0]} alt={p.name} className="w-12 h-12 object-cover rounded-sm" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-wine-dark truncate font-semibold">{p.name}</p>
                                        <p className="text-xs text-wine-dark/60">{p.region}</p>
                                    </div>
                                    <span className="label-overline text-vineyard">{p.sales_count || 0} un.</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function KpiCard({ label, value, growth, sub, highlight, icon: Icon }) {
    return (
        <div className={`p-6 rounded-md border shadow-sm ${highlight ? "bg-wine-dark text-cream border-wine-dark" : "bg-white border-gold-soft/30"}`}>
            <div className="flex items-start justify-between">
                <span className={`label-overline ${highlight ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                {Icon && <Icon className={`h-4 w-4 ${highlight ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />}
            </div>
            <p className="font-heading text-3xl md:text-4xl mt-3 leading-none">{value}</p>
            <div className="mt-3 flex items-center gap-2">
                {growth != null && (
                    <span className={`flex items-center gap-1 text-xs font-semibold ${growth >= 0 ? "text-vineyard" : "text-destructive"}`}>
                        {growth >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {growth >= 0 ? "+" : ""}{growth}%
                    </span>
                )}
                {sub && <span className={`text-xs ${highlight ? "text-cream/60" : "text-wine-dark/55"}`}>{sub}</span>}
            </div>
        </div>
    );
}

function Legend({ color, label }) {
    return (
        <span className="flex items-center gap-2 text-wine-dark/60">
            <span className="w-3 h-3 rounded-sm" style={{ background: color }} />
            {label}
        </span>
    );
}

function ActivityItem({ item }) {
    const map = {
        order: { icon: ShoppingBag, color: "text-wine" },
        producer: { icon: Store, color: "text-gold-deep" },
        subscription: { icon: Wine, color: "text-vineyard" },
    };
    const cfg = map[item.type] || map.order;
    const Icon = cfg.icon;
    return (
        <div className="flex items-start gap-3 py-2.5 border-b border-wine-dark/5 last:border-0">
            <div className={`p-2 rounded-md bg-cream ${cfg.color}`}>
                <Icon className="h-4 w-4" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-sm text-wine-dark font-semibold truncate">{item.title}</p>
                <p className="text-xs text-wine-dark/60">{item.subtitle}</p>
            </div>
            <StatusBadge status={item.status} />
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        paid: { label: "Paga", color: "text-vineyard bg-vineyard/10" },
        active: { label: "Ativa", color: "text-vineyard bg-vineyard/10" },
        approved: { label: "Aprovado", color: "text-vineyard bg-vineyard/10" },
        pending: { label: "Pendente", color: "text-gold-deep bg-gold-soft/20" },
        pending_payment: { label: "Aguarda", color: "text-gold-deep bg-gold-soft/20" },
        cancelled: { label: "Cancelada", color: "text-destructive bg-destructive/10" },
        paused: { label: "Pausada", color: "text-wine-dark/60 bg-wine-dark/5" },
    };
    const cfg = map[status] || { label: status || "—", color: "text-wine-dark/60 bg-wine-dark/5" };
    return (
        <span className={`label-overline px-2 py-1 rounded text-[10px] ${cfg.color}`}>
            {cfg.label}
        </span>
    );
}

/* ---------- PRODUCERS ---------- */
function ProducersAdmin() {
    const nav = useNavigate();
    const [list, setList] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const load = () => api.get("/admin/producers").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);

    async function approve(e, id) {
        e.stopPropagation();
        await api.post(`/producers/${id}/approve`);
        toast.success("Produtor aprovado");
        load();
    }

    const filtered = list.filter((p) => {
        if (filter === "pending" && p.approved) return false;
        if (filter === "approved" && !p.approved) return false;
        if (q && !p.producer_name.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    return (
        <Section title="Produtores" subtitle={`${list.length} total · ${list.filter(p => !p.approved).length} pendentes`}>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <SearchInput value={q} onChange={setQ} placeholder="Procurar produtor..." />
                <Tabs value={filter} onChange={setFilter} options={[
                    { value: "all", label: "Todos" },
                    { value: "pending", label: "Pendentes" },
                    { value: "approved", label: "Aprovados" },
                ]} />
            </div>
            <DataTable>
                <thead><tr className="text-left label-overline text-wine-dark/60"><th className="py-3 px-4">Nome</th><th>Região</th><th>Estado</th><th>Criado</th><th></th><th></th></tr></thead>
                <tbody>
                    {filtered.map((p) => (
                        <tr
                            key={p.id}
                            onClick={() => nav(`/admin/produtores/${p.id}`)}
                            className="border-t border-wine-dark/5 hover:bg-cream cursor-pointer transition-colors"
                            data-testid={`producer-row-${p.id}`}
                        >
                            <td className="py-4 px-4">
                                <p className="font-heading text-lg text-wine-dark">{p.producer_name}</p>
                            </td>
                            <td className="text-sm text-wine-dark/70">{p.region}</td>
                            <td><StatusBadge status={p.approved ? "approved" : "pending"} /></td>
                            <td className="text-xs text-wine-dark/60">{p.created_at && new Date(p.created_at).toLocaleDateString("pt-PT")}</td>
                            <td className="px-2">
                                {!p.approved && (
                                    <button onClick={(e) => approve(e, p.id)} className="btn-wine text-xs px-4 py-2 inline-flex items-center gap-1" data-testid={`approve-producer-${p.id}`}>
                                        <Check className="h-3 w-3" /> Aprovar
                                    </button>
                                )}
                            </td>
                            <td className="px-4 text-wine"><ArrowUpRight className="h-4 w-4" /></td>
                        </tr>
                    ))}
                    {filtered.length === 0 && (
                        <tr><td colSpan="6" className="text-center py-10 text-wine-dark/60">Sem produtores.</td></tr>
                    )}
                </tbody>
            </DataTable>
        </Section>
    );
}

/* ---------- PRODUCTS ---------- */
function ProductsAdmin() {
    const nav = useNavigate();
    const [list, setList] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");

    const load = () => {
        const status = filter === "all" ? "" : filter;
        api.get("/admin/products", { params: status ? { status } : {} }).then((r) => setList(r.data));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    async function approve(e, id) {
        e.stopPropagation();
        await api.post(`/products/${id}/approve`);
        toast.success("Produto aprovado");
        load();
    }

    const filtered = list.filter((p) =>
        !q || p.name.toLowerCase().includes(q.toLowerCase())
    );

    return (
        <Section title="Produtos" subtitle={`${list.length} no catálogo`}>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <SearchInput value={q} onChange={setQ} placeholder="Procurar produto..." />
                <Tabs value={filter} onChange={setFilter} options={[
                    { value: "all", label: "Todos" },
                    { value: "pending", label: "Pendentes" },
                    { value: "approved", label: "Aprovados" },
                    { value: "out_of_stock", label: "Sem stock" },
                ]} />
            </div>
            <DataTable>
                <thead><tr className="text-left label-overline text-wine-dark/60"><th className="py-3 px-4">Produto</th><th className="px-2">Categoria</th><th className="px-2">Região</th><th className="text-right px-3">Valor produtor</th><th className="text-right px-3">Comissão (15%)</th><th className="text-right px-3">Preço venda</th><th className="px-3">Stock</th><th className="px-2">Estado</th><th></th><th></th></tr></thead>
                <tbody>
                    {filtered.map((p) => {
                        const producer = p.producer_price != null ? p.producer_price : p.price * 0.85;
                        const commission = p.price - producer;
                        return (
                        <tr
                            key={p.id}
                            onClick={() => nav(`/admin/produtos/${p.id}`)}
                            className="border-t border-wine-dark/5 hover:bg-cream cursor-pointer transition-colors"
                            data-testid={`product-row-${p.id}`}
                        >
                            <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                    {p.images?.[0] ? (
                                        <img src={p.images[0]} alt={p.name} className="w-12 h-12 object-cover rounded-sm flex-shrink-0" />
                                    ) : (
                                        <div className="w-12 h-12 bg-cream-warm rounded-sm flex items-center justify-center flex-shrink-0">
                                            <Package className="h-5 w-5 text-wine/40" strokeWidth={1.5} />
                                        </div>
                                    )}
                                    <div className="min-w-0">
                                        <p className="font-heading text-base text-wine-dark truncate">{p.name}</p>
                                        {p.limited_edition && (
                                            <span className="label-overline text-[9px] text-gold-deep">Edição limitada</span>
                                        )}
                                    </div>
                                </div>
                            </td>
                            <td className="text-xs text-wine-dark/70">{p.category}</td>
                            <td className="text-xs text-wine-dark/70">{p.region}</td>
                            <td className="text-right px-3 text-sm text-vineyard font-semibold whitespace-nowrap">€{producer.toFixed(2)}</td>
                            <td className="text-right px-3 text-sm text-gold-deep whitespace-nowrap">€{commission.toFixed(2)}</td>
                            <td className="text-right px-3 font-heading text-base text-wine-dark whitespace-nowrap">€{p.price.toFixed(2)}</td>
                            <td className={`text-sm px-3 ${p.stock === 0 ? "text-destructive font-semibold" : "text-wine-dark/70"}`}>{p.stock}</td>
                            <td><StatusBadge status={p.approved ? "approved" : "pending"} /></td>
                            <td className="px-2">
                                {!p.approved && (
                                    <button onClick={(e) => approve(e, p.id)} className="btn-wine text-xs px-4 py-2 inline-flex items-center gap-1" data-testid={`approve-product-${p.id}`}>
                                        <Check className="h-3 w-3" /> Aprovar
                                    </button>
                                )}
                            </td>
                            <td className="px-4 text-wine"><ArrowUpRight className="h-4 w-4" /></td>
                        </tr>
                        );
                    })}
                    {filtered.length === 0 && (
                        <tr><td colSpan="10" className="text-center py-10 text-wine-dark/60">Sem produtos nesta categoria.</td></tr>
                    )}
                </tbody>
            </DataTable>
        </Section>
    );
}

/* ---------- ORDERS ---------- */
function OrdersAdmin() {
    const [list, setList] = useState([]);
    const [selected, setSelected] = useState(null);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    useEffect(() => { api.get("/admin/orders").then((r) => setList(r.data)); }, []);

    const filtered = list.filter((o) => {
        if (filter !== "all" && o.status !== filter) return false;
        if (q && !o.id.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    return (
        <Section title="Encomendas" subtitle={`${list.length} no total`}>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <SearchInput value={q} onChange={setQ} placeholder="Procurar #id..." />
                <Tabs value={filter} onChange={setFilter} options={[
                    { value: "all", label: "Todas" },
                    { value: "paid", label: "Pagas" },
                    { value: "pending_payment", label: "Pendentes" },
                ]} />
            </div>
            <DataTable>
                <thead><tr className="text-left label-overline text-wine-dark/60"><th className="py-3 px-4">Pedido</th><th>Data</th><th>Itens</th><th>Total</th><th>Comissão</th><th>Estado</th><th></th></tr></thead>
                <tbody>
                    {filtered.map((o) => (
                        <tr key={o.id} className="border-t border-wine-dark/5 hover:bg-cream cursor-pointer transition-colors" onClick={() => setSelected(o)}>
                            <td className="py-4 px-4 font-mono text-xs text-wine-dark">#{o.id.slice(0, 8)}</td>
                            <td className="text-xs text-wine-dark/60">{new Date(o.created_at).toLocaleDateString("pt-PT")}</td>
                            <td className="text-sm text-wine-dark/70">{o.items.length}</td>
                            <td className="font-heading text-base text-wine-dark">€{o.total.toFixed(2)}</td>
                            <td className="text-sm text-gold-deep">€{(o.platform_commission || 0).toFixed(2)}</td>
                            <td><StatusBadge status={o.status} /></td>
                            <td className="px-4 text-wine"><ArrowUpRight className="h-4 w-4" /></td>
                        </tr>
                    ))}
                    {filtered.length === 0 && (
                        <tr><td colSpan="7" className="text-center py-10 text-wine-dark/60">Sem encomendas.</td></tr>
                    )}
                </tbody>
            </DataTable>
            {selected && <OrderDrawer order={selected} onClose={() => setSelected(null)} />}
        </Section>
    );
}

function OrderDrawer({ order, onClose }) {
    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-elegant/40 backdrop-blur-sm" onClick={onClose} data-testid="order-drawer">
            <div className="bg-cream w-full max-w-xl h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <span className="label-overline text-wine">Encomenda</span>
                        <h2 className="font-heading text-3xl text-wine-dark mt-1">#{order.id.slice(0, 8)}</h2>
                        <p className="text-xs text-wine-dark/60 mt-1">{new Date(order.created_at).toLocaleString("pt-PT")}</p>
                    </div>
                    <button onClick={onClose} className="text-wine-dark/60 hover:text-wine" data-testid="drawer-close">
                        <X className="h-5 w-5" />
                    </button>
                </div>
                <StatusBadge status={order.status} />

                <div className="mt-8 bg-white border border-gold-soft/30 rounded-md p-5 space-y-3">
                    <h3 className="label-overline text-wine">Itens</h3>
                    {order.items.map((it, i) => (
                        <div key={`${it.product_id || "i"}-${i}`} className="flex items-center justify-between text-sm border-b border-wine-dark/5 last:border-0 pb-2 last:pb-0">
                            <div className="flex-1">
                                <p className="text-wine-dark">{it.name}</p>
                                <p className="text-xs text-wine-dark/60">{it.quantity} × €{it.price.toFixed(2)}</p>
                            </div>
                            <span className="font-heading text-base text-wine-dark">€{it.subtotal.toFixed(2)}</span>
                        </div>
                    ))}
                </div>

                <div className="mt-6 bg-white border border-gold-soft/30 rounded-md p-5 space-y-2 text-sm">
                    <RowKV label="Subtotal" value={`€${order.subtotal.toFixed(2)}`} />
                    {order.discount > 0 && <RowKV label="Desconto" value={`-€${order.discount.toFixed(2)}`} />}
                    <RowKV label="Envio" value={order.shipping === 0 ? "Grátis" : `€${order.shipping.toFixed(2)}`} />
                    <div className="hairline my-3" />
                    <RowKV label="Total" value={`€${order.total.toFixed(2)}`} bold />
                    <RowKV label="Comissão plataforma" value={`€${(order.platform_commission || 0).toFixed(2)}`} accent />
                </div>

                {order.shipping_address && (
                    <div className="mt-6 bg-white border border-gold-soft/30 rounded-md p-5">
                        <h3 className="label-overline text-wine mb-3">Envio para</h3>
                        <p className="text-sm text-wine-dark">{order.shipping_address.full_name}</p>
                        <p className="text-sm text-wine-dark/70">{order.shipping_address.street}</p>
                        <p className="text-sm text-wine-dark/70">{order.shipping_address.postal_code} {order.shipping_address.city}</p>
                        {order.tracking_code && (
                            <p className="text-xs text-wine mt-3 label-overline">Tracking: {order.tracking_code}</p>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function RowKV({ label, value, bold, accent }) {
    return (
        <div className="flex justify-between">
            <span className={accent ? "text-gold-deep" : "text-wine-dark/70"}>{label}</span>
            <span className={`${bold ? "font-heading text-lg text-wine-dark" : ""} ${accent ? "text-gold-deep font-semibold" : ""}`}>{value}</span>
        </div>
    );
}

/* ---------- USERS ---------- */
function UsersAdmin() {
    const [list, setList] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    useEffect(() => { api.get("/admin/users").then((r) => setList(r.data)); }, []);

    const filtered = list.filter((u) => {
        if (filter !== "all" && u.role !== filter) return false;
        if (q && !`${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    return (
        <Section title="Utilizadores" subtitle={`${list.length} total`}>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <SearchInput value={q} onChange={setQ} placeholder="Procurar nome ou email..." />
                <Tabs value={filter} onChange={setFilter} options={[
                    { value: "all", label: "Todos" },
                    { value: "customer", label: "Clientes" },
                    { value: "vendor", label: "Produtores" },
                    { value: "admin", label: "Admins" },
                ]} />
            </div>
            <DataTable>
                <thead><tr className="text-left label-overline text-wine-dark/60"><th className="py-3 px-4">Nome</th><th>Email</th><th>Tipo</th><th>Criado</th></tr></thead>
                <tbody>
                    {filtered.map((u) => (
                        <tr key={u.id} className="border-t border-wine-dark/5 hover:bg-cream transition-colors">
                            <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-wine/10 text-wine rounded-full flex items-center justify-center text-xs font-semibold">
                                        {u.name?.charAt(0).toUpperCase()}
                                    </div>
                                    <span className="font-semibold text-wine-dark">{u.name}</span>
                                </div>
                            </td>
                            <td className="text-sm text-wine-dark/70">{u.email}</td>
                            <td>
                                <span className={`label-overline px-2 py-1 rounded text-[10px] ${
                                    u.role === "admin" ? "bg-wine text-cream" :
                                    u.role === "vendor" ? "bg-gold-soft/20 text-gold-deep" :
                                    "bg-vineyard/10 text-vineyard"
                                }`}>{u.role}</span>
                            </td>
                            <td className="text-xs text-wine-dark/60">{u.created_at && new Date(u.created_at).toLocaleDateString("pt-PT")}</td>
                        </tr>
                    ))}
                </tbody>
            </DataTable>
        </Section>
    );
}

/* ---------- SUBSCRIPTIONS ---------- */
function SubsAdmin() {
    const [list, setList] = useState([]);
    useEffect(() => { api.get("/admin/subscriptions").then((r) => setList(r.data)); }, []);
    const totalMrr = list.filter(s => s.status === "active").reduce((a, b) => a + b.price, 0);
    return (
        <Section title="Assinaturas Clube" subtitle={`${list.filter(s => s.status === 'active').length} ativas · MRR €${totalMrr.toFixed(2)}`}>
            <DataTable>
                <thead><tr className="text-left label-overline text-wine-dark/60"><th className="py-3 px-4">Plano</th><th>Preço</th><th>Estado</th><th>Iniciada</th><th>Renovação</th></tr></thead>
                <tbody>
                    {list.map((s) => (
                        <tr key={s.id} className="border-t border-wine-dark/5 hover:bg-cream transition-colors">
                            <td className="py-3 px-4 font-heading text-base text-wine-dark">{s.plan_name}</td>
                            <td className="text-sm">€{s.price.toFixed(2)}/mês</td>
                            <td><StatusBadge status={s.status} /></td>
                            <td className="text-xs text-wine-dark/60">{s.started_at ? new Date(s.started_at).toLocaleDateString("pt-PT") : "—"}</td>
                            <td className="text-xs text-wine-dark/60">{s.next_renewal ? new Date(s.next_renewal).toLocaleDateString("pt-PT") : "—"}</td>
                        </tr>
                    ))}
                    {list.length === 0 && (
                        <tr><td colSpan="5" className="text-center py-10 text-wine-dark/60">Sem assinaturas.</td></tr>
                    )}
                </tbody>
            </DataTable>
        </Section>
    );
}

/* ---------- COUPONS ---------- */
function CouponsAdmin() {
    const [list, setList] = useState([]);
    const [form, setForm] = useState({ code: "", discount_pct: 10 });
    const load = () => api.get("/coupons").then((r) => setList(r.data));
    useEffect(() => { load(); }, []);

    async function create(e) {
        e.preventDefault();
        await api.post("/coupons", { code: form.code.toUpperCase(), discount_pct: parseFloat(form.discount_pct), active: true });
        toast.success("Cupão criado");
        setForm({ code: "", discount_pct: 10 });
        load();
    }

    return (
        <Section title="Cupões de desconto" subtitle={`${list.length} cupões`}>
            <form onSubmit={create} className="bg-white border border-gold-soft/30 rounded-md p-5 flex flex-wrap gap-4 items-end mb-6 shadow-sm">
                <label className="flex-1 min-w-[160px]">
                    <span className="label-overline text-wine-dark">Código</span>
                    <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required placeholder="VERAO20" className="mt-2 w-full bg-transparent border border-wine-dark/20 px-3 py-2.5 rounded-md text-sm focus:border-wine" data-testid="coupon-code" />
                </label>
                <label className="w-32">
                    <span className="label-overline text-wine-dark">% Desconto</span>
                    <input type="number" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: e.target.value })} required min="1" max="100" className="mt-2 w-full bg-transparent border border-wine-dark/20 px-3 py-2.5 rounded-md text-sm focus:border-wine" data-testid="coupon-pct" />
                </label>
                <button className="btn-wine" data-testid="create-coupon">Criar cupão</button>
            </form>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((c) => (
                    <div key={c.id} className="bg-white border border-gold-soft/30 rounded-md p-5 relative overflow-hidden" data-testid={`coupon-card-${c.code}`}>
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gold-soft/10 rounded-bl-full" />
                        <Tag className="h-5 w-5 text-gold-deep relative" strokeWidth={1.5} />
                        <p className="font-heading text-3xl text-wine-dark mt-3">{c.code}</p>
                        <p className="text-sm text-wine-dark/60 mt-1">{c.discount_pct}% de desconto</p>
                        <div className="mt-4">
                            <StatusBadge status={c.active ? "active" : "paused"} />
                        </div>
                    </div>
                ))}
            </div>
        </Section>
    );
}

/* ---------- WINES (specialized) ---------- */
const WINE_CATEGORIES = ["Vinho Tinto", "Vinho Branco", "Vinho do Porto", "Espumante", "Rosé"];

function WinesAdmin() {
    const nav = useNavigate();
    const [list, setList] = useState([]);
    const [q, setQ] = useState("");
    const [type, setType] = useState("all");
    const [region, setRegion] = useState("all");

    useEffect(() => {
        api.get("/admin/products").then((r) => {
            setList(r.data.filter((p) => WINE_CATEGORIES.includes(p.category)));
        });
    }, []);

    async function approve(e, id) {
        e.stopPropagation();
        await api.post(`/products/${id}/approve`);
        toast.success("Vinho aprovado");
        const { data } = await api.get("/admin/products");
        setList(data.filter((p) => WINE_CATEGORIES.includes(p.category)));
    }

    // Aggregates
    const regions = Array.from(new Set(list.map((p) => p.region))).sort();
    const filtered = list.filter((p) => {
        if (type !== "all" && p.category !== type) return false;
        if (region !== "all" && p.region !== region) return false;
        if (q && !p.name.toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    const byType = WINE_CATEGORIES.map((c) => ({
        type: c,
        count: list.filter((p) => p.category === c).length,
    })).filter((x) => x.count > 0);

    const byRegion = regions.map((r) => ({
        region: r,
        count: list.filter((p) => p.region === r).length,
        avg_price: (() => {
            const ps = list.filter((p) => p.region === r);
            return ps.length ? ps.reduce((s, p) => s + p.price, 0) / ps.length : 0;
        })(),
    }));

    const totalUnits = list.reduce((s, p) => s + (p.sales_count || 0), 0);
    const avgPrice = list.length ? list.reduce((s, p) => s + p.price, 0) / list.length : 0;
    const limitedCount = list.filter((p) => p.limited_edition).length;
    const outOfStock = list.filter((p) => p.stock === 0).length;
    const totalStock = list.reduce((s, p) => s + p.stock, 0);
    const topSelling = [...list].sort((a, b) => (b.sales_count || 0) - (a.sales_count || 0)).slice(0, 5);

    return (
        <div className="space-y-8">
            <div>
                <span className="label-overline text-wine">Garrafeira</span>
                <h2 className="font-heading text-4xl text-wine-dark mt-2">Vinhos do Caseirinho</h2>
                <p className="text-sm text-wine-dark/60 mt-2">
                    Curadoria editorial — tintos, brancos, do Porto, espumantes e rosés portugueses.
                </p>
            </div>

            {/* KPI Strip */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Kpi icon={Wine} label="Garrafeira total" value={list.length} sub={`${totalStock} garrafas em stock`} highlight />
                <Kpi icon={Sprout} label="Regiões" value={regions.length} sub="DOC portuguesas" />
                <Kpi icon={Tag} label="Edições limitadas" value={limitedCount} />
                <Kpi icon={TrendingUp} label="Preço médio" value={`€${avgPrice.toFixed(2)}`} />
                <Kpi icon={ShoppingBag} label="Vendidos" value={totalUnits} sub={`${outOfStock} esgotados`} />
            </div>

            {/* Two charts: by category + by region */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                    <span className="label-overline text-wine/60">Distribuição</span>
                    <h3 className="font-heading text-2xl text-wine-dark mt-1 mb-5">Por tipo</h3>
                    <div className="space-y-3">
                        {byType.map((t) => {
                            const pct = list.length ? Math.round((t.count / list.length) * 100) : 0;
                            return (
                                <button
                                    key={t.type}
                                    onClick={() => setType(t.type === type ? "all" : t.type)}
                                    className={`w-full text-left ${type === t.type ? "ring-1 ring-wine rounded-md p-2 -m-2" : ""}`}
                                >
                                    <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-sm text-wine-dark font-semibold">{t.type}</span>
                                        <span className="text-xs text-wine-dark/60">{t.count} · {pct}%</span>
                                    </div>
                                    <div className="h-2 bg-cream-warm rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all duration-500 ${
                                                t.type === "Vinho Tinto" ? "bg-wine" :
                                                t.type === "Vinho Branco" ? "bg-gold-soft" :
                                                t.type === "Vinho do Porto" ? "bg-wine-dark" :
                                                t.type === "Espumante" ? "bg-vineyard" : "bg-wine-soft"
                                            }`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                    <span className="label-overline text-wine/60">Terroir</span>
                    <h3 className="font-heading text-2xl text-wine-dark mt-1 mb-5">Por região</h3>
                    {byRegion.length === 0 ? (
                        <p className="text-wine-dark/60 text-sm">Sem dados.</p>
                    ) : (
                        <div className="space-y-3">
                            {byRegion.map((r) => (
                                <button
                                    key={r.region}
                                    onClick={() => setRegion(r.region === region ? "all" : r.region)}
                                    className={`w-full flex items-center justify-between py-2 px-3 rounded-md hover:bg-cream transition-colors ${region === r.region ? "bg-cream" : ""}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <Sprout className="h-4 w-4 text-vineyard" strokeWidth={1.5} />
                                        <span className="font-heading text-base text-wine-dark">{r.region}</span>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-sm text-wine-dark font-semibold">{r.count}</span>
                                        <p className="text-xs text-wine-dark/60">média €{r.avg_price.toFixed(2)}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Top selling wines */}
            {topSelling.length > 0 && topSelling[0].sales_count > 0 && (
                <div className="bg-white rounded-md border border-gold-soft/30 p-6 shadow-sm">
                    <div className="flex items-end justify-between mb-5">
                        <div>
                            <span className="label-overline text-wine/60">Best-sellers</span>
                            <h3 className="font-heading text-2xl text-wine-dark mt-1">Vinhos mais vendidos</h3>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                        {topSelling.map((p, i) => (
                            <Link
                                to={`/admin/produtos/${p.id}`}
                                key={p.id}
                                className="border border-gold-soft/20 rounded-md overflow-hidden hover:border-wine transition-colors group relative"
                            >
                                <div className="absolute top-2 left-2 z-10 w-7 h-7 bg-wine text-cream font-heading rounded-full flex items-center justify-center text-sm">
                                    {i + 1}
                                </div>
                                <div className="aspect-square bg-cream-warm overflow-hidden">
                                    {p.images?.[0] ? (
                                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center"><Wine className="h-10 w-10 text-wine/30" /></div>
                                    )}
                                </div>
                                <div className="p-3">
                                    <p className="text-xs text-wine-dark/60">{p.region}</p>
                                    <h4 className="font-heading text-sm text-wine-dark leading-tight mt-1 line-clamp-2">{p.name}</h4>
                                    <p className="text-xs text-vineyard label-overline mt-2">{p.sales_count || 0} vendidos</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Filters + list */}
            <div>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                    <SearchInput value={q} onChange={setQ} placeholder="Procurar vinho..." />
                    <Tabs value={type} onChange={setType} options={[
                        { value: "all", label: "Todos" },
                        ...byType.map((t) => ({ value: t.type, label: t.type })),
                    ]} />
                    {regions.length > 0 && (
                        <select
                            value={region}
                            onChange={(e) => setRegion(e.target.value)}
                            className="bg-white border border-gold-soft/30 rounded-md px-3 py-2.5 text-xs label-overline text-wine-dark focus:border-wine"
                            data-testid="wine-region-select"
                        >
                            <option value="all">Todas regiões</option>
                            {regions.map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    )}
                    <span className="ml-auto text-xs text-wine-dark/60 label-overline">
                        {filtered.length} {filtered.length === 1 ? "vinho" : "vinhos"}
                    </span>
                </div>
                <DataTable>
                    <thead><tr className="text-left label-overline text-wine-dark/60"><th className="py-3 px-4">Vinho</th><th className="px-2">Tipo</th><th className="px-2">Região</th><th className="text-right px-3">Valor produtor</th><th className="text-right px-3">Comissão (15%)</th><th className="text-right px-3">Preço venda</th><th className="px-3">Stock</th><th className="px-2">Vendas</th><th className="px-2">Estado</th><th></th><th></th></tr></thead>
                    <tbody>
                        {filtered.map((p) => {
                            const producer = p.price * 0.85;
                            const commission = p.price * 0.15;
                            return (
                            <tr
                                key={p.id}
                                onClick={() => nav(`/admin/produtos/${p.id}`)}
                                className="border-t border-wine-dark/5 hover:bg-cream cursor-pointer transition-colors"
                                data-testid={`wine-row-${p.id}`}
                            >
                                <td className="py-3 px-4">
                                    <div className="flex items-center gap-3">
                                        {p.images?.[0] ? (
                                            <img src={p.images[0]} alt={p.name} className="w-12 h-14 object-cover rounded-sm flex-shrink-0" />
                                        ) : (
                                            <div className="w-12 h-14 bg-cream-warm rounded-sm flex items-center justify-center flex-shrink-0">
                                                <Wine className="h-5 w-5 text-wine/40" strokeWidth={1.5} />
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <p className="font-heading text-base text-wine-dark truncate">{p.name}</p>
                                            {p.limited_edition && (
                                                <span className="label-overline text-[9px] text-gold-deep">Edição limitada</span>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span className={`label-overline text-[9px] px-2 py-1 rounded ${
                                        p.category === "Vinho Tinto" ? "bg-wine/10 text-wine-dark" :
                                        p.category === "Vinho Branco" ? "bg-gold-soft/20 text-gold-deep" :
                                        p.category === "Vinho do Porto" ? "bg-wine-dark/10 text-wine-dark" :
                                        p.category === "Espumante" ? "bg-vineyard/10 text-vineyard" :
                                        "bg-wine-soft/10 text-wine-soft"
                                    }`}>{p.category}</span>
                                </td>
                                <td className="text-xs text-wine-dark/70">{p.region}</td>
                                <td className="text-right px-3 text-sm text-vineyard font-semibold whitespace-nowrap">€{producer.toFixed(2)}</td>
                                <td className="text-right px-3 text-sm text-gold-deep whitespace-nowrap">€{commission.toFixed(2)}</td>
                                <td className="text-right px-3 font-heading text-base text-wine-dark whitespace-nowrap">€{p.price.toFixed(2)}</td>
                                <td className={`text-sm px-3 ${p.stock === 0 ? "text-destructive font-semibold" : "text-wine-dark/70"}`}>{p.stock}</td>
                                <td className="text-sm text-vineyard label-overline">{p.sales_count || 0}</td>
                                <td><StatusBadge status={p.approved ? "approved" : "pending"} /></td>
                                <td className="px-2">
                                    {!p.approved && (
                                        <button onClick={(e) => approve(e, p.id)} className="btn-wine text-xs px-4 py-2 inline-flex items-center gap-1">
                                            <Check className="h-3 w-3" /> Aprovar
                                        </button>
                                    )}
                                </td>
                                <td className="px-4 text-wine"><ArrowUpRight className="h-4 w-4" /></td>
                            </tr>
                            );
                        })}
                        {filtered.length === 0 && (
                            <tr><td colSpan="11" className="text-center py-10 text-wine-dark/60">Nenhum vinho corresponde aos filtros.</td></tr>
                        )}
                    </tbody>
                </DataTable>
            </div>
        </div>
    );
}

function Kpi({ icon: Icon, label, value, sub, highlight }) {
    return (
        <div className={`p-5 rounded-md border shadow-sm ${highlight ? "bg-wine-dark text-cream border-wine-dark" : "bg-white border-gold-soft/30"}`}>
            <div className="flex justify-between items-start">
                <span className={`label-overline ${highlight ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                <Icon className={`h-4 w-4 ${highlight ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />
            </div>
            <p className="font-heading text-3xl mt-2 leading-none">{value}</p>
            {sub && <span className={`text-xs mt-2 inline-block ${highlight ? "text-cream/60" : "text-wine-dark/55"}`}>{sub}</span>}
        </div>
    );
}

/* ---------- SHARED UI ---------- */
function Section({ title, subtitle, children }) {
    return (
        <div>
            <div className="mb-6">
                <h2 className="font-heading text-4xl text-wine-dark">{title}</h2>
                {subtitle && <p className="text-sm text-wine-dark/60 mt-2">{subtitle}</p>}
            </div>
            {children}
        </div>
    );
}

function DataTable({ children }) {
    return (
        <div className="bg-white border border-gold-soft/30 rounded-md shadow-sm overflow-x-auto">
            <table className="w-full">{children}</table>
        </div>
    );
}

function SearchInput({ value, onChange, placeholder }) {
    return (
        <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wine/50" strokeWidth={1.5} />
            <input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full bg-white border border-gold-soft/30 rounded-md pl-10 pr-3 py-2.5 text-sm focus:border-wine"
                data-testid="admin-search"
            />
        </div>
    );
}

function Tabs({ value, onChange, options }) {
    return (
        <div className="inline-flex bg-white border border-gold-soft/30 rounded-md p-1" data-testid="admin-tabs">
            {options.map((opt) => (
                <button
                    key={opt.value}
                    onClick={() => onChange(opt.value)}
                    className={`label-overline px-4 py-2 rounded text-[10px] transition-all ${
                        value === opt.value ? "bg-wine text-cream" : "text-wine-dark hover:bg-wine/5"
                    }`}
                    data-testid={`tab-${opt.value}`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );
}
